import {
	TICKET_ASSIGNMENT_CATEGORY,
	TICKET_REPAIR_REPORT_REQUIRED_CATEGORIES,
	TICKET_WORKFLOW_STATUS,
} from '../../constants';
import { areTicketQueuesEnabled } from './areTicketQueuesEnabled';
import { hasRemoteTicketPhoto, isRemoteTicketPhotoUrl } from './ticketPhotoUrls';

const REQUIRED_REPAIR_REPORT_FIELDS = [
	{ field: 'repairWorkCarriedOut', label: 'Work carried out' },
	{ field: 'repairOutcome', label: 'Outcome' },
	{ field: 'repairCompletedByName', label: 'Completed by' },
	{ field: 'repairCompletedAt', label: 'Completed date', hasValue: value => Boolean(value) },
	// A selected/local file is not a completed upload; resolution requires the same public URL shape as ticket photos.
	{ field: 'repairReportUrl', label: 'Uploaded report', hasValue: isRemoteTicketPhotoUrl },
];

const hasTrimmedValue = value => Boolean(String(value || '').trim());

export const getTicketWorkflowStatus = (ticket, tenantSettings) => (
	areTicketQueuesEnabled(tenantSettings)
		? ticket?.ticketWorkflowStatus || TICKET_WORKFLOW_STATUS.ACTIVE
		: TICKET_WORKFLOW_STATUS.ACTIVE
);

export const isTicketIncoming = (ticket, tenantSettings) => getTicketWorkflowStatus(ticket, tenantSettings) === TICKET_WORKFLOW_STATUS.INCOMING;

export const isTicketRepairReportRequired = ticket => (
	TICKET_REPAIR_REPORT_REQUIRED_CATEGORIES.includes(ticket?.ticketAssignmentCategory)
);

export const getMissingTicketRepairReportFields = ticket => {
	return REQUIRED_REPAIR_REPORT_FIELDS
		.filter(({ field, hasValue = hasTrimmedValue }) => !hasValue(ticket?.[field]))
		.map(({ label }) => label);
};

const getId = value => value?._id || value?.id || value;

const idsMatch = (left, right) => {
	const leftId = getId(left);
	const rightId = getId(right);

	return Boolean(leftId && rightId && String(leftId) === String(rightId));
};

// Technician work belongs on a service run only when that run is assigned to the same person.
export const isTicketDriverListVisible = (ticket, tenantSettings, serviceRunDriver) => {
	if (!areTicketQueuesEnabled(tenantSettings)) {
		return Boolean(ticket?.visibleToDrivers && !ticket?.archived);
	}

	const isDriverWork = (
		(!ticket?.ticketAssignmentCategory || ticket.ticketAssignmentCategory === TICKET_ASSIGNMENT_CATEGORY.DRIVER) &&
		ticket?.visibleToDrivers
	);
	const isAssignedTechnicianWork = (
		ticket?.ticketAssignmentCategory === TICKET_ASSIGNMENT_CATEGORY.TECHNICIAN &&
		idsMatch(ticket.assignee, serviceRunDriver)
	);

	return (
		getTicketWorkflowStatus(ticket, tenantSettings) === TICKET_WORKFLOW_STATUS.ACTIVE &&
		(isDriverWork || isAssignedTechnicianWork) &&
		!ticket?.archived
	);
};

export const hasTicketRepairReport = ticket => getMissingTicketRepairReportFields(ticket).length === 0;

export const getTicketResolutionBlockers = (ticket, tenantSettings) => {
	if (!ticket) {
		return ['Ticket details are unavailable'];
	}

	const blockers = [];
	if (isTicketIncoming(ticket, tenantSettings)) {
		blockers.push('Triage this ticket before resolving it.');
	}

	if (ticket.photoRequired && !hasRemoteTicketPhoto(ticket.imageUrls)) {
		blockers.push('Upload a photo before resolving this ticket.');
	}

	const missingRepairFields = isTicketRepairReportRequired(ticket)
		? getMissingTicketRepairReportFields(ticket)
		: [];
	if (missingRepairFields.length) {
		blockers.push(`Complete the repair report before resolving this ticket. Missing: ${missingRepairFields.join(', ')}.`);
	}

	return blockers;
};

export const getTicketResolutionBlocker = (ticket, tenantSettings) => (
	getTicketResolutionBlockers(ticket, tenantSettings).join(' ') || null
);

export const canResolveTicket = (ticket, tenantSettings) => getTicketResolutionBlockers(ticket, tenantSettings).length === 0;
